import React, { useState, useEffect } from "react";
import SEO from "../seo";

const CODFormsOnXPage = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "cod-options",
        "publishing",
        "custom-fields",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="COD Forms on XPage | xPage Help Center"
        description="Learn about COD Forms on XPage."
        keywords="XPage, COD Forms"
        canonical="https://help.xpage.ai/help/business-expansion/COD%20Forms%20on%20XPage"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Overview */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            COD Forms on XPage
          </h1>
          <p className="text-sm text-systemLabel">
            XPage provides three Cash on Delivery (COD) form options to help you
            optimize conversions based on your sales strategy.
          </p>
          <p className="text-sm text-systemLabel mt-3">
            By default, the standard COD checkout form is automatically
            activated once you enable Cash on Delivery in{" "}
            <strong>Settings</strong> → <strong>Payments</strong>. This form
            replaces the regular checkout and is ready to use without additional
            setup.
          </p>
        </header>

        {/* COD Options */}
        <section id="cod-options" className="mb-6 scroll-mt-20">
          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4">
            <p className="text-sm text-systemLabel mb-4">
              In addition to the default checkout form, you can choose between
              two advanced COD formats:
            </p>

            <ul className="list-disc list-inside space-y-2 text-sm text-systemLabel mb-4">
              <li>
                <strong>COD Popup Form</strong> – A conversion-focused popup
                that appears on your landing page.
              </li>
              <li>
                <strong>COD Embedded Form</strong> – A form directly integrated
                within your landing page layout.
              </li>
            </ul>

            <p className="text-sm text-systemLabel">
              These additional forms must be activated manually from your
              settings.
            </p>
          </div>
        </section>

        {/* Publishing Requirement */}
        <section id="publishing" className="mb-6 scroll-mt-20">
          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4">
            <p className="text-sm text-systemLabel">
              To link a landing page with either the COD Popup or the COD
              Embedded form, the landing page must be published. Draft pages
              cannot be connected to advanced COD forms. Once the page is live,
              you can assign the selected COD format and begin receiving orders
              through it.
            </p>
          </div>
        </section>

        {/* Custom Fields */}
        <section id="custom-fields" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-3">
            Adding Custom Fields to Your COD Forms
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4">
            <p className="text-sm text-systemLabel mb-4">
              You can collect additional customer information by adding custom
              fields to your checkout form.
            </p>

            <p className="text-sm text-systemLabel mb-2">To do this:</p>

            <ol className="list-decimal list-inside space-y-2 text-sm text-systemLabel ml-4 mb-4">
              <li>
                Go to <strong>Settings</strong> → <strong>Checkout</strong>
              </li>
              <li>
                Scroll down to <strong>Custom Fields</strong>
              </li>
              <li>
                Add a new field by defining:
                <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                  <li>
                    <strong>Label</strong> – The field name customers will see
                    (e.g., “Delivery Notes”)
                  </li>
                  <li>
                    <strong>Placeholder</strong> – The helper text displayed
                    inside the input field
                  </li>
                </ul>
              </li>
            </ol>

            <p className="text-sm text-systemLabel">
              Once saved, the custom fields will automatically appear on your
              active COD forms.
            </p>

            <p className="text-sm text-systemLabel mt-3">
              This allows you to tailor your checkout experience based on your
              operational needs, such as collecting location details, delivery
              instructions, or any additional required information.
            </p>
          </div>
        </section>
      </div>

      {/* Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            {[
              { id: "overview", label: "Overview" },
              { id: "cod-options", label: "COD Options" },
              { id: "publishing", label: "Publishing Requirement" },
              { id: "custom-fields", label: "Custom Fields" },
            ].map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={`block py-1 transition-colors border-l-2 pl-3 ${
                    activeSection === item.id
                      ? "text-deepPurple font-semibold border-deepPurple"
                      : "border-transparent hover:text-deepPurple"
                  }`}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CODFormsOnXPage;
